import { mergeCachedRecord } from './cacheMerge';
import db from './db';
import { mergeMonotonicMarker } from './read_cursor';
import { mergeAgentChatWithGenerationTurn } from './widgetGenerationTurns';

export function directMessageWorkspaceSummary(directMessage) {
  if (!directMessage?.workspace_id) return null;

  const peerProfile = directMessage.peer_profile;

  return {
    id: String(directMessage.workspace_id),
    kind: 'direct_message',
    title: peerProfile.full_name,
    main_channel_id: directMessage.main_channel_id ? String(directMessage.main_channel_id) : null,
    subscription_tier: directMessage.subscription_tier || 'free',
    subscription_status: 'unsubscribed',
    // Passed through raw: isAvCallingEnabled reads a missing field as on, matching the column default.
    av_calling_enabled: directMessage.av_calling_enabled,
    flexicon: peerProfile.flexicon,
    background_image: directMessage.background_image || null,
    background_image_url: directMessage.background_image_url || null,
  };
}

function normalizeDirectMessage(directMessage) {
  if (!directMessage?.id) return null;

  return {
    ...directMessage,
    id: String(directMessage.id),
    workspace_id: directMessage.workspace_id ? String(directMessage.workspace_id) : null,
    main_channel_id: directMessage.main_channel_id ? String(directMessage.main_channel_id) : null,
    current_profile_id: directMessage.current_profile_id ? String(directMessage.current_profile_id) : null,
  };
}

function normalizeScopedRow(row, workspaceId = row?.workspace_id) {
  if (!row?.id || !workspaceId) return null;

  return {
    ...row,
    id: String(row.id),
    workspace_id: String(workspaceId),
  };
}

function collectDirectMessageRows(directMessages) {
  const normalizedDirectMessages = [];
  const channels = [];
  const profiles = [];
  const readMarkers = [];

  // Direct message workspaces never go into db.workspaces, but their workspace-
  // scoped child rows still use the normal stores so existing board/widget/chat
  // components can run without a parallel DM-only implementation.
  const widgets = [];
  const boardItems = [];
  const agentChats = [];
  const widgetSnapshots = new Map();
  const boardItemSnapshots = new Map();
  const agentChatSnapshots = new Map();

  for (const directMessage of directMessages || []) {
    const normalized = normalizeDirectMessage(directMessage);
    if (!normalized) continue;

    normalizedDirectMessages.push(normalized);

    const mainChannel = normalizeScopedRow(directMessage.main_channel, normalized.workspace_id);
    if (mainChannel) {
      channels.push({
        ...mainChannel,
        id: normalized.main_channel_id || mainChannel.id,
        workspace_id: normalized.workspace_id,
      });
    }

    const currentProfile = normalizeScopedRow(directMessage.current_profile, normalized.workspace_id);
    const peerProfile = normalizeScopedRow(directMessage.peer_profile, normalized.workspace_id);
    if (currentProfile) profiles.push(currentProfile);
    if (peerProfile) profiles.push(peerProfile);

    const readMarker = normalizeScopedRow(directMessage.read_marker, normalized.workspace_id);
    if (readMarker) readMarkers.push(readMarker);

    if (Array.isArray(directMessage.widgets)) {
      const keepIds = new Set();
      for (const widget of directMessage.widgets) {
        const normalizedWidget = normalizeScopedRow(widget, normalized.workspace_id);
        if (!normalizedWidget) continue;

        widgets.push(normalizedWidget);
        keepIds.add(normalizedWidget.id);
      }
      widgetSnapshots.set(normalized.workspace_id, keepIds);
    }

    if (Array.isArray(directMessage.board_items)) {
      const keepIds = new Set();
      for (const boardItem of directMessage.board_items) {
        const normalizedBoardItem = normalizeScopedRow(boardItem, normalized.workspace_id);
        if (!normalizedBoardItem) continue;

        boardItems.push(normalizedBoardItem);
        keepIds.add(normalizedBoardItem.id);
      }
      boardItemSnapshots.set(normalized.workspace_id, keepIds);
    }

    if (Array.isArray(directMessage.agent_chats)) {
      const keepIds = new Set();
      for (const agentChat of directMessage.agent_chats) {
        const normalizedAgentChat = normalizeScopedRow(agentChat, normalized.workspace_id);
        if (!normalizedAgentChat) continue;

        agentChats.push(normalizedAgentChat);
        keepIds.add(normalizedAgentChat.id);
      }
      agentChatSnapshots.set(normalized.workspace_id, keepIds);
    }
  }

  return {
    normalizedDirectMessages,
    channels,
    profiles,
    readMarkers,
    widgets,
    boardItems,
    agentChats,
    widgetSnapshots,
    boardItemSnapshots,
    agentChatSnapshots,
  };
}

async function pruneScopedSnapshotRows(table, workspaceId, keepIds) {
  // Only route-hydration payloads include full resource arrays. When present,
  // treat them as authoritative snapshots for that DM workspace; list payloads
  // omit the arrays and therefore skip pruning.
  const rows = await table.where('workspace_id').equals(workspaceId).toArray();
  const deleteKeys = rows.filter((row) => !keepIds.has(String(row.id))).map((row) => [row.workspace_id, row.id]);
  if (deleteKeys.length > 0) await table.bulkDelete(deleteKeys);
}

async function bulkPutMergedRows(table, rows, keyForRow, nestedKeys = []) {
  if (rows.length === 0) return;

  const existing = await table.bulkGet(rows.map(keyForRow));
  await table.bulkPut(rows.map((row, index) => mergeCachedRecord(existing[index], row, nestedKeys)));
}

export async function storeDirectMessagePayload(directMessage) {
  const [normalized] = await storeDirectMessagePayloads([directMessage]);
  return normalized || null;
}

export async function storeDirectMessagePayloads(directMessages) {
  const {
    normalizedDirectMessages,
    channels,
    profiles,
    readMarkers,
    widgets,
    boardItems,
    agentChats,
    widgetSnapshots,
    boardItemSnapshots,
    agentChatSnapshots,
  } = collectDirectMessageRows(directMessages);
  if (normalizedDirectMessages.length === 0) return [];

  await db.transaction(
    'rw',
    db.direct_messages,
    db.channels,
    db.profiles,
    db.channel_read_markers,
    db.widgets,
    db.board_items,
    db.agent_chats,
    async () => {
      await bulkPutMergedRows(db.direct_messages, normalizedDirectMessages, (directMessage) => directMessage.id, [
        'main_channel',
        'peer_profile',
        'current_profile',
      ]);
      await bulkPutMergedRows(db.channels, channels, (channel) => [channel.workspace_id, channel.id]);
      await bulkPutMergedRows(db.profiles, profiles, (profile) => [profile.workspace_id, profile.id]);
      if (readMarkers.length > 0) {
        const existingMarkers = await db.channel_read_markers.bulkGet(readMarkers.map((marker) => [marker.workspace_id, marker.id]));
        const mergedReadMarkers = readMarkers.map((marker, index) => mergeMonotonicMarker(existingMarkers[index], marker));
        await db.channel_read_markers.bulkPut(mergedReadMarkers);
      }
      if (widgets.length > 0) await db.widgets.bulkPut(widgets);
      if (boardItems.length > 0) await db.board_items.bulkPut(boardItems);
      if (agentChats.length > 0) {
        const existingAgentChats = await db.agent_chats.bulkGet(agentChats.map((agentChat) => [agentChat.workspace_id, agentChat.id]));
        const mergedAgentChats = agentChats.map((agentChat, index) =>
          mergeAgentChatWithGenerationTurn(existingAgentChats[index], agentChat),
        );
        await db.agent_chats.bulkPut(mergedAgentChats);
      }
      for (const [workspaceId, keepIds] of widgetSnapshots) await pruneScopedSnapshotRows(db.widgets, workspaceId, keepIds);
      for (const [workspaceId, keepIds] of boardItemSnapshots) await pruneScopedSnapshotRows(db.board_items, workspaceId, keepIds);
      for (const [workspaceId, keepIds] of agentChatSnapshots) await pruneScopedSnapshotRows(db.agent_chats, workspaceId, keepIds);
    },
  );

  return normalizedDirectMessages;
}

export async function replaceVisibleDirectMessages(directMessages) {
  const normalized = await storeDirectMessagePayloads(directMessages);
  const keepIds = new Set(normalized.map((directMessage) => directMessage.id));
  const existing = await db.direct_messages.toArray();
  const staleVisibleIds = existing
    .filter((directMessage) => directMessage.visible !== false && !keepIds.has(String(directMessage.id)))
    .map((directMessage) => directMessage.id);

  if (staleVisibleIds.length > 0) await db.direct_messages.bulkDelete(staleVisibleIds);

  return normalized;
}

export async function directMessageWorkspaceIdSet() {
  const directMessages = await db.direct_messages.toArray();
  return new Set(
    directMessages
      .map((directMessage) => directMessage.workspace_id)
      .filter((workspaceId) => workspaceId != null)
      .map((workspaceId) => String(workspaceId)),
  );
}
